import { API_BASE_URL } from './httpClient';
import { playAudioSafely, resumeAudioContext, isUnlocked } from './iosAudioHelper';
/**
 * Web Audio API Service for Hearen
 * Handles all audio generation for tests and therapy
 */

import { audioSessionManager } from './audioSessionManager';

// iOS detection
const isIOSDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || window.Capacitor !== undefined;
};

class AudioService {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.oscillator = null;
    this.audioSource = null; // MediaElementAudioSourceNode
    this.audioElement = null; // HTML Audio Element
    this.noiseNode = null;
    this.currentPanner = null; // Add reference to current panner
    this.isInitialized = false;
    this.isIOS = isIOSDevice();
    this.isPaused = false; // Track pause state for true pause/resume
    this.pausedNoiseVolume = undefined; // Store noise volume during pause
    
    // V1.4 iOS Web Audio Buffer Engine (same as radioService)
    // Uses fetch → decodeAudioData → BufferSourceNode (no HTMLMediaElement on iOS)
    this.iosSoundSource = null;        // Current BufferSourceNode for nature sounds
    this.iosSoundBuffer = null;        // Current decoded AudioBuffer
    this.iosFetchController = null;    // AbortController for fetch cancellation
    this.iosCurrentVolume = 0.8;       // Track current volume for iOS
    this.iosIsLooping = true;          // Track loop state for iOS
    this.iosOnEndedCallback = null;    // Callback when sound ends (non-loop)
    
    // V1.5 iOS Pause/Resume support
    this.iosStartTime = 0;             // When playback started (audioContext.currentTime)
    this.iosPausedAt = 0;              // Position in buffer when paused (seconds)
    this.iosIsPaused = false;          // Track iOS-specific pause state
    
    // Equalizer system
    this.inputNode = null; // NEW: Input node BEFORE equalizer (for capturing original signal)
    this.equalizerNode = null; // Node where all filters connect
    this.equalizerFilters = []; // Array of BiquadFilterNodes
    this.makeupGainNode = null; // Gain node after filters to prevent clipping
    this.equalizerSettings = null; // User's equalizer settings
    this.audiometricCompensationEnabled = true; // Track if audiometric compensation is enabled (default true to match frontend)
    this.inverseNotchEnabled = true; // Track if inverse notch is enabled (default true to match frontend)
    
    // Spectrum analyzers for visualization
    this.analyserOriginal = null; // Analyser for original signal (before EQ)
    this.analyserProcessed = null; // Analyser for processed signal (after EQ)
  }

  /**
   * Initialize Web Audio Context
   */
  init() {
    if (this.isInitialized) return;
    
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Detect iOS for CORS handling
    const ua = navigator.userAgent.toLowerCase();
    this.isIOS = /iphone|ipad|ipod/.test(ua) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // iOS: Register stop callback for audio session manager
    audioSessionManager.registerStopCallback('therapy', () => {
      console.log('[AudioService] Stop requested by AudioSessionManager');
      this.stopTherapy();
    });
    
    // BUILD 55: Register resume callback for foreground resume (therapy/soundscape)
    audioSessionManager.registerResumeCallback('therapy', () => {
      return this.resumeTherapyIfNeeded();
    });
    audioSessionManager.registerResumeCallback('soundscape', () => {
      return this.resumeTherapyIfNeeded();
    });
    
    // Create spectrum analysers for visualization
    this.analyserOriginal = this.audioContext.createAnalyser();
    this.analyserOriginal.fftSize = 256; // 128 frequency bins
    this.analyserOriginal.smoothingTimeConstant = 0.8;
    
    this.analyserProcessed = this.audioContext.createAnalyser();
    this.analyserProcessed.fftSize = 256; // 128 frequency bins
    this.analyserProcessed.smoothingTimeConstant = 0.8;
    
    // Create input node (connects to sources and captures original signal)
    this.inputNode = this.audioContext.createGain();
    this.inputNode.gain.value = 1.0;
    
    // Create equalizer node (receives from input node)
    this.equalizerNode = this.audioContext.createGain();
    this.equalizerNode.gain.value = 1.0; // No attenuation
    
    // ========== SOUNDSCAPE LIMITER (Volume normalization with Radio) ==========
    // DynamicsCompressor as soft limiter to match Radio Hearen volume levels
    this.soundscapeLimiterNode = this.audioContext.createDynamicsCompressor();
    this.soundscapeLimiterNode.threshold.value = -3.0; // Start limiting at -3 dB
    this.soundscapeLimiterNode.knee.value = 3.0; // Soft knee (3 dB)
    this.soundscapeLimiterNode.ratio.value = 12.0; // Moderate ratio for natural sound
    this.soundscapeLimiterNode.attack.value = 0.003; // 3ms attack (fast)
    this.soundscapeLimiterNode.release.value = 0.050; // 50ms release (smooth)
    console.log('[AudioService] ✅ Soundscape limiter created for volume normalization');
    // ==========================================================================
    
    // Create master gain
    this.masterGain = this.audioContext.createGain();
    this.masterGain.gain.value = 0; // Start at 0 - will be controlled by React UI slider with smooth fade-in
    
    // CRITICAL: Connect limiter -> masterGain -> speakers
    this.soundscapeLimiterNode.connect(this.masterGain);
    this.masterGain.connect(this.audioContext.destination);
    
    // Audio routing architecture (with limiter):
    // Source -> inputNode (captures original via analyserOriginal in parallel)
    //            |
    //            +-> analyserOriginal (parallel connection - captures ORIGINAL signal for visualization)
    //            |
    //            +-> equalizerNode -> [filters] -> analyserProcessed -> LIMITER -> masterGain -> destination
    //
    // The connection from equalizerNode onwards will be established by loadEqualizer()
    
    // Connect input to analyserOriginal (parallel, for monitoring only - does NOT affect main audio path)
    this.inputNode.connect(this.analyserOriginal);
    
    // Connect input to equalizer (main audio path)
    this.inputNode.connect(this.equalizerNode);
    
    // NOTE: We do NOT create a default path from equalizerNode to analyserProcessed here
    // This prevents parallel paths when filters are loaded
    // The path will be established when loadEqualizer() is called
    
    
    this.isInitialized = true;
  }

  /**
   * V1.1.1: Ensure the canonical Hearen audio graph exists.
   * Creates permanent bus nodes (musicBusGain, voiceProgramGain, voiceUserGain)
   * that persist for the lifetime of the AudioContext.
   * 
   * Graph:
   *   musicSource → musicBusGain → inputNode → [equalizer] → [limiter] → masterGain → destination
   *   voiceSource → voiceProgramGain → voiceUserGain → masterGain → destination
   *
   * @returns {{ musicBusGain: GainNode, voiceProgramGain: GainNode, voiceUserGain: GainNode, masterGain: GainNode }}
   */
  ensureAudioGraph() {
    if (!this.isInitialized) this.init();
    
    if (this._hearenGraph) return this._hearenGraph;
    
    const ctx = this.audioContext;
    
    // Music bus gain — ducking target (1.0 = normal, 0.3 = ducked)
    const musicBusGain = ctx.createGain();
    musicBusGain.gain.value = 1.0;
    musicBusGain.connect(this.inputNode); // music → equalizer chain → masterGain → destination
    
    // Voice program gain — for fade in/out during playback
    const voiceProgramGain = ctx.createGain();
    voiceProgramGain.gain.value = 1.0;
    
    // Voice user gain — controlled by voice volume slider
    const voiceUserGain = ctx.createGain();
    voiceUserGain.gain.value = 0.40; // Default 40%
    
    // Connect voice chain: voiceProgramGain → voiceUserGain → masterGain → destination
    voiceProgramGain.connect(voiceUserGain);
    voiceUserGain.connect(this.masterGain);
    
    this._hearenGraph = { musicBusGain, voiceProgramGain, voiceUserGain, masterGain: this.masterGain };
    
    console.log('[AudioService] ✅ Hearen audio graph created (musicBus + voiceBus → masterGain)');
    console.log('[AudioService]   musicBusGain → inputNode → EQ → limiter → masterGain → destination');
    console.log('[AudioService]   voiceProgramGain → voiceUserGain → masterGain → destination');
    
    return this._hearenGraph;
  }


  /**
   * Resume audio context if suspended (required for user interaction)
   */
  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Load and apply user's equalizer settings
   * @param {object} equalizerSettings - Equalizer configuration from API
   * @param {boolean} enableAudiometricCompensation - Enable/disable audiometric compensation
   * @param {boolean} enableInverseNotch - Enable/disable inverse notch
   */
  loadEqualizer(equalizerSettings, enableAudiometricCompensation = true, enableInverseNotch = true) {
    this.init();
    
    // Clear existing filters
    this.clearEqualizer();
    
    this.equalizerSettings = equalizerSettings;
    // Store toggle states
    this.audiometricCompensationEnabled = enableAudiometricCompensation;
    this.inverseNotchEnabled = enableInverseNotch;
    
    if (!equalizerSettings) {
      // CRITICAL: Even without equalizer settings, we must establish a complete audio path
      // Otherwise audio will be mute!
      try {
        this.equalizerNode.disconnect();
        this.analyserProcessed.disconnect();
        this.soundscapeLimiterNode.disconnect();
        this.masterGain.disconnect();
      } catch (e) {
        // Already disconnected
      }
      
      // Create default path: equalizerNode -> analyserProcessed -> limiter -> masterGain -> destination
      this.equalizerNode.connect(this.analyserProcessed);
      this.analyserProcessed.connect(this.soundscapeLimiterNode);
      this.soundscapeLimiterNode.connect(this.masterGain);
      this.masterGain.connect(this.audioContext.destination);
      return;
    }
    
    console.log('📊 Loading equalizer settings:', equalizerSettings);
    console.log(`   Audiometric: ${enableAudiometricCompensation}, Inverse Notch: ${enableInverseNotch}`);
    
    // CRITICAL: Disconnect equalizerNode from default routing to avoid parallel paths
    try {
      this.equalizerNode.disconnect();
    } catch (e) {
      // Already disconnected, that's fine
    }
    
    // CRITICAL: Disconnect analyserProcessed from any previous connections
    try {
      this.analyserProcessed.disconnect();
    } catch (e) {
      // Already disconnected
    }
    
    // CRITICAL: Disconnect masterGain to rebuild the entire chain
    try {
      this.masterGain.disconnect();
    } catch (e) {
      // Already disconnected
    }
    
    let previousNode = this.equalizerNode;
    
    // Apply audiometric compensation (peaking filters for each frequency)
    if (enableAudiometricCompensation && equalizerSettings.has_audiometric_compensation && equalizerSettings.audiometric_gains) {
      
      for (const gainData of equalizerSettings.audiometric_gains) {
        if (gainData.gain > 0) { // Only boost if there's hearing loss
          const filter = this.audioContext.createBiquadFilter();
          filter.type = 'peaking';
          filter.frequency.value = gainData.frequency;
          filter.Q.value = 1.0; // Moderate bandwidth
          // Limit gain to avoid saturation (max 15 dB instead of raw threshold value)
          const targetGain = Math.min(gainData.gain * 0.5, 15); // Apply 50% of the gain, max 15 dB
          filter.gain.value = targetGain;
          filter.targetGain = targetGain; // Store for toggle
          
          previousNode.connect(filter);
          previousNode = filter;
          this.equalizerFilters.push(filter);
          
          console.log(`  ✓ ${gainData.frequency} Hz: +${targetGain.toFixed(1)} dB (from ${gainData.gain} dB threshold)`);
        }
      }
    }
    
    // Apply tinnitus filter (notch or inverse notch based on Lab Mode program)
    if (enableInverseNotch && equalizerSettings.has_inverse_notch && equalizerSettings.inverse_notch) {
      const notch = equalizerSettings.inverse_notch;
      const isNotch = notch.gain < 0;
      const isInverseNotch = notch.gain > 0;
      
      
      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'peaking';
      filter.frequency.value = notch.center_frequency;
      filter.Q.value = notch.center_frequency / notch.bandwidth; // Q = center / bandwidth
      filter.gain.value = notch.gain; // Can be negative (notch) or positive (inverse notch)
      filter.targetGain = notch.gain; // Store for toggle
      
      previousNode.connect(filter);
      previousNode = filter;
      this.equalizerFilters.push(filter);
      
      const gainSign = notch.gain > 0 ? '+' : '';
      console.log(`  ✓ ${notch.center_frequency} Hz (±${notch.bandwidth/2} Hz): ${gainSign}${notch.gain} dB`);
    }
    
    // Keep equalizerNode at full gain - let the filters do their job
    this.equalizerNode.gain.setValueAtTime(1.0, this.audioContext.currentTime);
    
    // Connect final filter chain to analysers, limiter and master gain
    if (this.equalizerFilters.length > 0) {
      // CRITICAL: Only the PROCESSED signal (after all filters) goes to analyserProcessed
      // previousNode is the LAST filter in the chain
      
      // CRITICAL ROUTING: previousNode -> analyserProcessed -> limiter -> masterGain -> destination
      previousNode.connect(this.analyserProcessed);
      this.analyserProcessed.connect(this.soundscapeLimiterNode);
      this.soundscapeLimiterNode.connect(this.masterGain);
      this.masterGain.connect(this.audioContext.destination);
      
    } else {
      // No filters active - connect equalizer directly to processed analyser
      // IMPORTANT: Still goes through analyserProcessed and limiter for consistency
      this.equalizerNode.connect(this.analyserProcessed);
      this.analyserProcessed.connect(this.soundscapeLimiterNode);
      this.soundscapeLimiterNode.connect(this.masterGain);
      this.masterGain.connect(this.audioContext.destination);
    }
  }

  /**
   * Clear all equalizer filters
   */
  clearEqualizer() {
    
    // Disconnect and clear all filters
    for (const filter of this.equalizerFilters) {
      try {
        filter.disconnect();
      } catch (e) {
        // Already disconnected
      }
    }
    this.equalizerFilters = [];
    
    // Disconnect makeup gain if exists
    if (this.makeupGainNode) {
      try {
        this.makeupGainNode.disconnect();
      } catch (e) {
        // Already disconnected
      }
      this.makeupGainNode = null;
    }
    
    // Disconnect analysers, limiter and master gain
    try {
      this.analyserProcessed.disconnect();
    } catch (e) {
      // Already disconnected
    }
    
    try {
      this.soundscapeLimiterNode.disconnect();
    } catch (e) {
      // Already disconnected
    }
    
    try {
      this.masterGain.disconnect();
    } catch (e) {
      // Already disconnected
    }
    
    // NOTE: We do NOT reconnect the default path here
    // The default path will be established when loadEqualizer is called next
    // or if no equalizer is needed, the path is: inputNode -> equalizerNode (not connected to destination)
    
  }

  /**
   * Get the correct output node (input node that connects to equalizer)
   * @returns {AudioNode} - Node to connect audio sources to
   */
  getOutputNode() {
    // Sources should connect to inputNode, which then feeds to equalizer
    return this.inputNode;
  }

  /**
   * Play a pure tone at specific frequency and volume
   * CRITICAL: Volume must be IDENTICAL for left and right
   * @param {number} frequency - Frequency in Hz
   * @param {number} volume - Volume 0-1
   * @param {string} ear - "left", "right", or "both"
   */
  async playTone(frequency, volume = 0.3, ear = "both", options = {}) {
    this.init();
    this.stopTone();

    const startTime = this.audioContext.currentTime + 0.01;

    await this.resume();

    console.log(`🎧 AUDIO START: freq=${frequency}Hz, volume=${volume.toFixed(6)}, ear="${ear}"`);

    this.oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    this.oscillator.type = 'sine';
    this.oscillator.frequency.setValueAtTime(frequency, startTime);

    const pulse = options?.pulse === true;

    if (pulse) {
      const pulseOn = 0.35;
      const pulseOff = 0.25;
      const cycle = pulseOn + pulseOff;

      gainNode.gain.setValueAtTime(0, startTime);

      for (let i = 0; i < 20; i++) {
        const t = startTime + i * cycle;

        gainNode.gain.setValueAtTime(0, t);
        gainNode.gain.linearRampToValueAtTime(volume, t + 0.03);
        gainNode.gain.setValueAtTime(volume, t + pulseOn);
        gainNode.gain.linearRampToValueAtTime(0, t + pulseOn + 0.03);
      }
    } else {
      gainNode.gain.setValueAtTime(volume, startTime);
    }

    this.oscillator.connect(gainNode);

    if (ear === "left" || ear === "right") {
      this.currentPanner = this.audioContext.createStereoPanner();
      const panValue = ear === "left" ? -1.0 : 1.0;
      this.currentPanner.pan.setValueAtTime(panValue, startTime);

      gainNode.connect(this.currentPanner);
      this.currentPanner.connect(this.masterGain);
    } else {
      gainNode.connect(this.masterGain);
      this.currentPanner = null;
    }

    this.masterGain.gain.setValueAtTime(1.0, startTime);

    this.oscillator.start(startTime);

    return this.oscillator;
  }

  /**
   * Stop currently playing tone - COMPLETE CLEANUP
   */
  stopTone() {
    if (this.oscillator) {
      try {
        this.oscillator.stop();
        this.oscillator.disconnect();
      } catch (e) {
        // Already stopped
      }
      this.oscillator = null;
    }
    if (this.currentPanner) {
      try {
        this.currentPanner.disconnect();
      } catch (e) {
        // Already disconnected
      }
      this.currentPanner = null;
    }
  }

  /**
   * Generate white noise
   */
  createWhiteNoise() {
    this.init();
    const bufferSize = 2 * this.audioContext.sampleRate;
    const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    
    return noiseBuffer;
  }

  /**
   * Generate pink noise (1/f noise)
   */
  createPinkNoise() {
    this.init();
    const bufferSize = 2 * this.audioContext.sampleRate;
    const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      output[i] *= 0.11; // Adjust volume
      b6 = white * 0.115926;
    }
    
    return noiseBuffer;
  }

  /**
   * Generate brown noise (Brownian noise)
   */
  createBrownNoise() {
    this.init();
    const bufferSize = 2 * this.audioContext.sampleRate;
    const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    
    let lastOut = 0.0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      output[i] = (lastOut + (0.02 * white)) / 1.02;
      lastOut = output[i];
      output[i] *= 3.5; // Adjust volume
    }
    
    return noiseBuffer;
  }

  /**
   * Create inverse notch filter (band-stop filter)
   * This removes a narrow frequency band from noise
   * @param {number} centerFrequency - Center frequency to notch out
   * @param {number} notchWidth - Width of the notch in Hz
   */
  createInverseNotchFilter(centerFrequency, notchWidth = 200) {
    this.init();
    
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'notch';
    filter.frequency.value = centerFrequency;
    filter.Q.value = centerFrequency / notchWidth; // Q = center_freq / bandwidth
    
    return filter;
  }

  // ========================================================================
  // V1.4 iOS WEB AUDIO BUFFER ENGINE FOR SOUNDSCAPES
  // Uses fetch → decodeAudioData → BufferSourceNode (no HTMLMediaElement)
  // This is required because createMediaElementSource breaks on iOS Safari
  // ========================================================================

  /**
   * iOS: Fetch and decode audio to buffer
   * @param {string} url - Audio URL
   * @returns {Promise<AudioBuffer>} - Decoded audio buffer
   */
  async iosFetchAndDecode(url) {
    console.log(`[AudioService] 📱 iOS: Fetching audio: ${url.split('/').pop()}`);
    
    // Cancel any previous fetch
    if (this.iosFetchController) {
      this.iosFetchController.abort();
    }
    this.iosFetchController = new AbortController();
    
    try {
      const response = await fetch(url, {
        signal: this.iosFetchController.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log(`[AudioService] 📱 iOS: Fetch OK, decoding...`);
      const arrayBuffer = await response.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log(`[AudioService] 📱 iOS: Decode OK, duration: ${audioBuffer.duration.toFixed(1)}s`);
      
      return audioBuffer;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[AudioService] 📱 iOS: Fetch aborted');
        return null;
      }
      console.error('[AudioService] 📱 iOS: Fetch/decode error:', error);
      throw error;
    }
  }

  /**
   * iOS: Play audio buffer via Web Audio API
   * @param {AudioBuffer} buffer - Decoded audio buffer
   * @param {number} volume - Volume 0-1
   * @param {boolean} loop - Whether to loop
   * @param {Function} onEnded - Callback when sound ends (if not looping)
   * @param {number} offset - Start position in seconds (for resume)
   */
  async iosPlayBuffer(buffer, volume = 0.8, loop = true, onEnded = null, offset = 0) {
    if (!buffer || !this.audioContext) {
      console.error('[AudioService] 📱 iOS: Missing buffer or audioContext');
      return;
    }
    
    // Stop current source if playing (but don't reset pause state if resuming)
    this.iosStopCurrentBuffer(offset > 0); // Pass flag to preserve pause tracking
    
    // CRITICAL: Ensure AudioContext is running (iOS Safari requirement)
    if (this.audioContext.state === 'suspended') {
      console.log('[AudioService] 📱 iOS: AudioContext suspended, resuming...');
      await resumeAudioContext(this.audioContext);
    }
    
    // Warn if audio not unlocked
    if (this.isIOS && !isUnlocked()) {
      console.warn('[AudioService] 📱 iOS: ⚠️ Audio may not be unlocked yet!');
    }
    
    // CRITICAL: Ensure audio path is complete (loadEqualizer establishes the chain)
    // If no equalizer loaded yet, establish default path
    if (!this.equalizerSettings) {
      this.loadEqualizer(null); // This creates: equalizerNode -> analyserProcessed -> masterGain -> destination
    }
    
    // Store settings
    this.iosCurrentVolume = volume;
    this.iosIsLooping = loop;
    this.iosOnEndedCallback = onEnded;
    this.iosIsPaused = false;
    
    // Create gain node for this source (separate from masterGain for individual volume control)
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = volume;
    
    // Create new buffer source
    this.iosSoundSource = this.audioContext.createBufferSource();
    this.iosSoundSource.buffer = buffer;
    this.iosSoundSource.loop = loop;
    this.iosSoundBuffer = buffer;
    
    // Connect: source → gain → inputNode → (equalizerNode → analyserProcessed → masterGain → destination)
    const outputNode = this.getOutputNode();
    this.iosSoundSource.connect(gainNode);
    gainNode.connect(outputNode);
    
    // CRITICAL: Set masterGain to allow sound through (it starts at 0)
    if (this.masterGain && this.masterGain.gain.value === 0) {
      this.masterGain.gain.value = 1.0;
      console.log('[AudioService] 📱 iOS: Set masterGain to 1.0');
    }
    
    // Store gain node reference for volume control
    this.iosGainNode = gainNode;
    
    // Handle sound end (for non-looping sounds)
    this.iosSoundSource.onended = () => {
      // Only trigger callback if not paused (pause also triggers onended)
      if (!this.iosIsPaused) {
        console.log(`[AudioService] 📱 iOS: Sound ended`);
        if (!loop && this.iosOnEndedCallback) {
          this.iosOnEndedCallback();
        }
      }
    };
    
    // Start playback (with offset for resume)
    try {
      // Record start time for pause position calculation
      this.iosStartTime = this.audioContext.currentTime - offset;
      
      // Start from offset position
      this.iosSoundSource.start(0, offset);
      this.isPlaying = true;
      // BUILD 52.8: Update playing state in session manager
      audioSessionManager.setPlaying('therapy', true);
      
      console.log(`[AudioService] 📱 iOS: ✅ Buffer playback started (loop=${loop}, volume=${(volume * 100).toFixed(0)}%, offset=${offset.toFixed(2)}s, contextState=${this.audioContext.state})`);
    } catch (error) {
      console.error('[AudioService] 📱 iOS: ❌ BufferSource start failed:', {
        error: error.name,
        message: error.message,
        contextState: this.audioContext.state
      });
    }
  }

  /**
   * iOS: Stop current buffer playback
   * @param {boolean} isResume - If true, don't reset pause tracking
   */
  iosStopCurrentBuffer(isResume = false) {
    if (this.iosSoundSource) {
      try {
        this.iosSoundSource.onended = null; // Prevent callback
        this.iosSoundSource.stop();
        this.iosSoundSource.disconnect();
      } catch (e) {
        // Ignore errors if already stopped
      }
      this.iosSoundSource = null;
    }
    if (this.iosGainNode) {
      try {
        this.iosGainNode.disconnect();
      } catch (e) {}
      this.iosGainNode = null;
    }
    
    // Only reset pause state if this is a full stop (not a resume)
    if (!isResume) {
      this.iosPausedAt = 0;
      this.iosStartTime = 0;
    }
  }

  /**
   * iOS: Pause buffer playback (saves position for resume)
   */
  iosPauseBuffer() {
    if (!this.iosSoundSource || !this.audioContext || !this.iosSoundBuffer) {
      console.log('[AudioService] 📱 iOS: Nothing to pause');
      return false;
    }
    
    // Calculate current position in the buffer
    const elapsed = this.audioContext.currentTime - this.iosStartTime;
    
    // For looping sounds, wrap around the buffer duration
    if (this.iosIsLooping && this.iosSoundBuffer) {
      this.iosPausedAt = elapsed % this.iosSoundBuffer.duration;
    } else {
      this.iosPausedAt = Math.min(elapsed, this.iosSoundBuffer.duration);
    }
    
    console.log(`[AudioService] 📱 iOS: Pausing at ${this.iosPausedAt.toFixed(2)}s`);
    
    // Mark as paused BEFORE stopping (so onended doesn't trigger callback)
    this.iosIsPaused = true;
    
    // Stop the buffer source (can't be paused, must be stopped and recreated)
    this.iosStopCurrentBuffer(true); // Pass true to preserve pause state
    
    return true;
  }

  /**
   * iOS: Resume buffer playback from paused position
   * @returns {boolean} Whether resume was successful
   */
  async iosResumeBuffer() {
    if (!this.iosSoundBuffer || !this.audioContext) {
      console.log('[AudioService] 📱 iOS: No buffer to resume');
      return false;
    }
    
    if (!this.iosIsPaused) {
      console.log('[AudioService] 📱 iOS: Not paused, nothing to resume');
      return false;
    }
    
    console.log(`[AudioService] 📱 iOS: Resuming from ${this.iosPausedAt.toFixed(2)}s`);
    
    // Play buffer from saved position
    await this.iosPlayBuffer(
      this.iosSoundBuffer,
      this.iosCurrentVolume,
      this.iosIsLooping,
      this.iosOnEndedCallback,
      this.iosPausedAt
    );
    
    return true;
  }

  /**
   * iOS: Set volume for current buffer playback
   * @param {number} volume - Volume 0-1
   */
  iosSetVolume(volume) {
    this.iosCurrentVolume = volume;
    if (this.iosGainNode && this.audioContext) {
      const currentTime = this.audioContext.currentTime;
      this.iosGainNode.gain.cancelScheduledValues(currentTime);
      this.iosGainNode.gain.setValueAtTime(this.iosGainNode.gain.value, currentTime);
      this.iosGainNode.gain.linearRampToValueAtTime(volume, currentTime + 0.05);
    }
  }

  /**
   * iOS: Clean up all buffers
   */
  iosCleanupBuffers() {
    console.log('[AudioService] 📱 iOS: Cleaning up buffers');
    
    // Cancel any pending fetch
    if (this.iosFetchController) {
      this.iosFetchController.abort();
      this.iosFetchController = null;
    }
    
    // Stop current playback
    this.iosStopCurrentBuffer();
    
    // Release buffer for garbage collection
    this.iosSoundBuffer = null;
    this.iosOnEndedCallback = null;
    
    // V1.5: Reset pause state
    this.iosIsPaused = false;
    this.iosPausedAt = 0;
    this.iosStartTime = 0;
  }

  // ========================================================================
  // END iOS WEB AUDIO BUFFER ENGINE
  // ========================================================================

  /**
   * Play therapy sound with equalizer
   * Can accept either:
   * - A file path directly (string starting with /)
   * - A therapy type ('pink_noise', 'brown_noise', 'white_noise', 'nature')
   * @param {string} therapyTypeOrPath - Therapy type or file path
   * @param {object} params - Therapy parameters
   */
  async playTherapy(therapyTypeOrPath, params = {}) {
    // BUILD 65: iOS Safari fix - acquire session fire-and-forget to not break gesture context
    // The audioContext.resume() in this.resume() MUST happen quickly
    console.log('[AudioService] Acquiring audio session for therapy...');
    audioSessionManager.acquireSession('therapy').catch(e => {
      console.warn('[AudioService] Session acquisition issue (non-blocking):', e);
    });
    
    this.stopTherapy(); // This will remove any existing event listeners
    this.init();
    await this.resume(); // This is the critical one - must resume AudioContext

    // Check if it's a file path (starts with / or http)
    if (typeof therapyTypeOrPath === 'string' && (therapyTypeOrPath.startsWith('/') || therapyTypeOrPath.startsWith('http'))) {
      
      // Get loop parameter (default true for backward compatibility)
      const loop = params.loop !== undefined ? params.loop : true;
      const onEnded = params.onEnded || null;
      const volume = params.volume !== undefined ? params.volume : 0.8;
      
      // Convert relative URLs to absolute with safe fallback
      let audioSrc = therapyTypeOrPath;
      const baseUrl = API_BASE_URL || 'https://hearen-backend.onrender.com';
      if (audioSrc && audioSrc.startsWith('/')) {
        audioSrc = `${baseUrl}${audioSrc}`;
        console.log(`🔄 [audioService] Normalized URL to: ${audioSrc}`);
      }
      
      // GUARD: Only allow http(s) URLs
      if (!audioSrc || !/^https?:\/\//i.test(audioSrc)) {
        console.error(`❌ [audioService] BLOCKED: base=${baseUrl}, original=${therapyTypeOrPath}, final=${audioSrc}`);
        return null;
      }
      
      // V1.4: iOS uses Web Audio Buffer Engine (no HTMLMediaElement)
      if (this.isIOS) {
        console.log(`[audioService] 📱 iOS: Using Web Audio Buffer Engine for file: ${audioSrc.split('/').pop()}`);
        try {
          const buffer = await this.iosFetchAndDecode(audioSrc);
          if (buffer) {
            this.iosPlayBuffer(buffer, volume, loop, onEnded);
            this.isPlaying = true;
          }
        } catch (error) {
          console.error('[audioService] 📱 iOS: Error playing file:', error);
        }
        // Return a pseudo audio element object for compatibility with TherapyPlayer
        return { 
          _iosBufferMode: true,
          paused: false,
          play: () => Promise.resolve(),
          pause: () => this.iosStopCurrentBuffer(),
        };
      }
      
      // Non-iOS: Use Audio element WITH Web Audio API for spectrum visualization
      if (!this.streamAudioElement) {
        this.streamAudioElement = new Audio();
        this.streamAudioElement.preload = 'auto';
        this.streamAudioElement.crossOrigin = 'anonymous'; // Required for Web Audio API
        console.log('[audioService V5] Created new stream audio element with crossOrigin');
        
        // Connect to Web Audio API for spectrum analyzer
        this.streamMediaSource = this.audioContext.createMediaElementSource(this.streamAudioElement);
        this.streamMediaSource.connect(this.inputNode);
        console.log('[audioService V5] Connected to Web Audio API for spectrum');
      }
      
      // Clean up previous state
      if (this.currentEndedHandler) {
        this.streamAudioElement.removeEventListener('ended', this.currentEndedHandler);
        this.currentEndedHandler = null;
      }
      
      // Stop any current playback
      this.streamAudioElement.pause();
      this.streamAudioElement.currentTime = 0;
      this.streamAudioElement.loop = loop;
      
      console.log('📂 [audioService V5] Playing:', audioSrc);
      this.streamAudioElement.src = audioSrc;
      
      // Attach onEnded callback if provided
      if (onEnded && !loop) {
        this.currentEndedHandler = onEnded;
        this.streamAudioElement.addEventListener('ended', this.currentEndedHandler);
      }
      
      try {
        await this.streamAudioElement.play();
        console.log('✅ [audioService V5] Playback started with spectrum');
        this.isPlaying = true;
      } catch (error) {
        if (error.name === 'AbortError') {
          return this.streamAudioElement;
        }
        console.error('❌ [audioService V5] Playback error:', error.name, error.message);
        throw error;
      }
      
      return this.streamAudioElement;
    }

    // Legacy therapy type handling
    const therapyType = therapyTypeOrPath;
    const { 
      volume = 0.3, 
      natureSoundName = null, 
      categoryPath = 'nature',
      customSoundId = null,
      isCustomSound = false,
      loop = true
    } = params;
    
    // Get the output node (either first equalizer filter or equalizerNode)
    const outputNode = this.getOutputNode();

    if (therapyType === 'pink_noise') {
      const noiseBuffer = this.createPinkNoise();
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      noiseSource.connect(gainNode);
      gainNode.connect(outputNode);

      noiseSource.start();
      this.noiseNode = noiseSource;

    } else if (therapyType === 'brown_noise') {
      const noiseBuffer = this.createBrownNoise();
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      noiseSource.connect(gainNode);
      gainNode.connect(outputNode);

      noiseSource.start();
      this.noiseNode = noiseSource;

    } else if (therapyType === 'white_noise') {
      const noiseBuffer = this.createWhiteNoise();
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      noiseSource.connect(gainNode);
      gainNode.connect(outputNode);

      noiseSource.start();
      this.noiseNode = noiseSource;

    } else if (therapyType === 'nature' && (natureSoundName || isCustomSound)) {
      // Load and play nature sound or custom sound
      console.log('🌊 Playing', isCustomSound ? 'custom sound' : 'nature sound', 
                  isCustomSound ? customSoundId : natureSoundName, 
                  'from category:', categoryPath);
      
      // Build path - custom sound or standard sound
      let soundPath;
      const baseUrl = API_BASE_URL || 'https://hearen-backend.onrender.com';
      if (isCustomSound && customSoundId) {
        soundPath = `${baseUrl}/api/custom-sounds/${customSoundId}/stream`;
      } else {
        soundPath = natureSoundName.includes('.') 
          ? `/sounds/${categoryPath}/${natureSoundName}` 
          : `/sounds/${categoryPath}/${natureSoundName}.wav`;
        console.log('🌳 Nature sound path:', soundPath);
      }
      
      // Normalize relative URLs to absolute
      if (soundPath && soundPath.startsWith('/')) {
        soundPath = `${baseUrl}${soundPath}`;
        console.log(`🔄 [audioService] Normalized sound URL to: ${soundPath}`);
      }
      
      // GUARD: Only allow http(s) URLs
      if (!soundPath || !/^https?:\/\//i.test(soundPath)) {
        console.error(`❌ [audioService] BLOCKED sound: base=${baseUrl}, original=${natureSoundName}, final=${soundPath}`);
        return null;
      }
      
      // Get onEnded callback if provided
      const onEnded = params.onEnded || null;
      
      // V1.4: iOS uses Web Audio Buffer Engine (no HTMLMediaElement)
      if (this.isIOS) {
        console.log(`[audioService] 📱 iOS: Using Web Audio Buffer Engine for nature sound`);
        try {
          const buffer = await this.iosFetchAndDecode(soundPath);
          if (buffer) {
            this.iosPlayBuffer(buffer, volume, loop, onEnded);
            this.isPlaying = true;
          }
        } catch (error) {
          console.error('[audioService] 📱 iOS: Error playing nature sound:', error);
        }
        // Return a pseudo audio element object for compatibility with TherapyPlayer
        return { 
          _iosBufferMode: true,
          paused: false,
          play: () => Promise.resolve(),
          pause: () => this.iosStopCurrentBuffer(),
        };
      }
      
      // Non-iOS: Use HTMLMediaElement + createMediaElementSource
      if (!this.audioElement) {
        this.audioElement = new Audio();
        this.audioSource = this.audioContext.createMediaElementSource(this.audioElement);
      }
      
      // Disconnect from old graph before reconnecting
      try {
        this.audioSource.disconnect();
      } catch (e) {}
      
      // Update source and volume
      this.audioElement.src = soundPath;
      this.audioElement.volume = volume;
      this.audioElement.loop = loop;
      
      // Attach onEnded callback if provided and loop is false
      if (onEnded && !loop) {
        this.currentEndedHandler = onEnded;
        this.audioElement.addEventListener('ended', this.currentEndedHandler);
      }
      
      // Connect to current output node (equalizer may have changed)
      this.audioSource.connect(outputNode);
      
      // Play
      try {
        await this.audioElement.play();
      } catch (e) {
        console.error('❌ Play failed:', e);
      }
    }
  }

  /**
   * Stop therapy playback
   */
  stopTherapy({ reason = 'unknown', forceStop = false } = {}) {
    console.log(`[AudioService] Stopping therapy playback... (reason: ${reason})`);
    
    // BUILD 52.8: Check if we should actually stop (not in background while playing)
    // Only block cleanup/unmount stops, allow user-initiated stops
    if (!forceStop && reason !== 'user' && !audioSessionManager.canSafelyCleanup('therapy') && this.isPlaying) {
      console.log(`[AudioService] ⚠️ NOT stopping (reason: ${reason}): audio is playing in background`);
      console.log('[AudioService] ⚠️ This is likely a page visibility/unmount event - keeping audio');
      return;
    }
    
    // iOS: Release audio session
    audioSessionManager.releaseSession('therapy');
    
    // BUILD 52.8: Update playing state in session manager with reason
    audioSessionManager.setPlaying('therapy', false, reason);
    
    // V1.4: Clean up iOS Web Audio buffers
    if (this.isIOS) {
      this.iosCleanupBuffers();
    }
    
    if (this.noiseNode) {
      try {
        this.noiseNode.stop();
        this.noiseNode.disconnect();
      } catch (e) {
        // Ignore - already stopped
      }
      this.noiseNode = null;
    }
    
    // V5: Stop stream audio element (used for file playback WITH Web Audio API)
    if (this.streamAudioElement) {
      if (this.currentEndedHandler) {
        this.streamAudioElement.removeEventListener('ended', this.currentEndedHandler);
        this.currentEndedHandler = null;
      }
      
      this.streamAudioElement.pause();
      try { this.streamAudioElement.currentTime = 0; } catch (e) { /* iOS may throw */ }
      this.streamAudioElement.removeAttribute('src');
      // NOTE: Do NOT call load() - causes iOS to use page URL as fallback
    }
    
    // Legacy V4: Stop simple audio element (fallback without Web Audio API)
    if (this.simpleAudioElement) {
      if (this.currentEndedHandler) {
        this.simpleAudioElement.removeEventListener('ended', this.currentEndedHandler);
        this.currentEndedHandler = null;
      }
      
      this.simpleAudioElement.pause();
      try { this.simpleAudioElement.currentTime = 0; } catch (e) { /* iOS may throw */ }
      this.simpleAudioElement.removeAttribute('src');
      // NOTE: Do NOT call load() - causes iOS to use page URL as fallback
    }
    
    // Legacy: Stop Web Audio API connected element
    if (this.audioElement) {
      if (this.currentEndedHandler) {
        this.audioElement.removeEventListener('ended', this.currentEndedHandler);
        this.currentEndedHandler = null;
      }
      
      this.audioElement.pause();
      try { this.audioElement.currentTime = 0; } catch (e) { /* iOS may throw */ }
      this.audioElement.removeAttribute('src');
      // NOTE: Do NOT call load() - causes iOS to use page URL as fallback
    }
    
    this.isPlaying = false;
    this.isPaused = false; // Reset pause state on full stop
  }

  /**
   * Pause therapy playback WITHOUT resetting position
   * Use this for true pause functionality
   */
  pauseTherapy() {
    console.log('[AudioService] 🔴 pauseTherapy() called - preserving position...');
    
    // BUILD 55: Mark as user-paused to prevent auto-resume on foreground
    audioSessionManager.setUserPaused('therapy', true);
    audioSessionManager.setUserPaused('soundscape', true);
    
    // V1.5: iOS uses buffer-based pause
    if (this.isIOS) {
      const paused = this.iosPauseBuffer();
      if (paused) {
        this.isPlaying = false;
        this.isPaused = true;
        console.log('[AudioService] 📱 iOS: ✅ Buffer paused');
        return;
      }
    }
    
    // Pause stream audio element (V5)
    if (this.streamAudioElement && !this.streamAudioElement.paused) {
      this.streamAudioElement.pause();
      console.log(`[AudioService] ✅ Stream audio paused at ${this.streamAudioElement.currentTime.toFixed(2)}s`);
    }
    
    // Pause simple audio element (legacy V4 fallback)
    if (this.simpleAudioElement && !this.simpleAudioElement.paused) {
      this.simpleAudioElement.pause();
      console.log(`[AudioService] Simple audio paused at ${this.simpleAudioElement.currentTime.toFixed(2)}s`);
    }
    
    // Pause Web Audio API connected element (legacy)
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
      console.log(`[AudioService] Audio element paused at ${this.audioElement.currentTime.toFixed(2)}s`);
    }
    
    // Suspend noise node (if white/pink noise is playing)
    // Note: Web Audio noise cannot be paused, only stopped
    // So we keep it running but mute it
    if (this.noiseNode && this.masterGain) {
      this.pausedNoiseVolume = this.masterGain.gain.value;
      this.masterGain.gain.setValueAtTime(0, this.audioContext.currentTime);
    }
    
    this.isPlaying = false;
    this.isPaused = true;
    console.log('[AudioService] ✅ isPaused set to TRUE');
  }

  /**
   * Resume therapy playback from paused position
   * iOS Safari FIX: Uses buffer recreation with offset for iOS
   * @returns {boolean} Whether resume was initiated (not necessarily completed)
   */
  resumeTherapy() {
    console.log('[AudioService] ▶️ resumeTherapy() called');
    
    // BUILD 55: Clear user-paused state since user is manually resuming
    audioSessionManager.setUserPaused('therapy', false);
    audioSessionManager.setUserPaused('soundscape', false);
    
    // V1.5: iOS uses buffer-based resume
    if (this.isIOS && this.iosIsPaused) {
      console.log('[AudioService] 📱 iOS: Using buffer resume...');
      // Call iosResumeBuffer synchronously in user gesture context
      // Use .then() pattern to maintain gesture context
      this.iosResumeBuffer()
        .then((success) => {
          if (success) {
            console.log('[AudioService] 📱 iOS: ✅ Buffer resumed');
            this.isPlaying = true;
            this.isPaused = false;
          }
        })
        .catch((e) => {
          console.error('[AudioService] 📱 iOS: ❌ Buffer resume failed:', e);
        });
      
      // Optimistically set state
      this.isPlaying = true;
      this.isPaused = false;
      return true;
    }
    
    // Resume noise node volume
    if (this.noiseNode && this.masterGain && this.pausedNoiseVolume !== undefined) {
      this.masterGain.gain.setValueAtTime(this.pausedNoiseVolume, this.audioContext.currentTime);
      this.pausedNoiseVolume = undefined;
    }
    
    // iOS Safari FIX: Call play() synchronously in user gesture, use .then() for state updates
    let resumed = false;
    
    // Try stream audio element first (V5)
    if (this.streamAudioElement && this.streamAudioElement.src && this.streamAudioElement.paused) {
      console.log('[AudioService] Resuming stream audio...');
      this.streamAudioElement.play()
        .then(() => {
          console.log(`[AudioService] ✅ Stream audio resumed from ${this.streamAudioElement.currentTime.toFixed(2)}s`);
          this.isPlaying = true;
          this.isPaused = false;
        })
        .catch((e) => {
          console.error('[AudioService] ❌ Failed to resume stream audio:', e);
        });
      resumed = true;
    }
    
    // Try simple audio element (legacy V4 fallback)
    if (!resumed && this.simpleAudioElement && this.simpleAudioElement.src && this.simpleAudioElement.paused) {
      console.log('[AudioService] Resuming simple audio...');
      this.simpleAudioElement.play()
        .then(() => {
          console.log(`[AudioService] ✅ Simple audio resumed from ${this.simpleAudioElement.currentTime.toFixed(2)}s`);
          this.isPlaying = true;
          this.isPaused = false;
        })
        .catch((e) => {
          console.error('[AudioService] ❌ Failed to resume simple audio:', e);
        });
      resumed = true;
    }
    
    // Try legacy audio element
    if (!resumed && this.audioElement && this.audioElement.src && this.audioElement.paused) {
      console.log('[AudioService] Resuming legacy audio element...');
      this.audioElement.play()
        .then(() => {
          console.log(`[AudioService] ✅ Audio element resumed from ${this.audioElement.currentTime.toFixed(2)}s`);
          this.isPlaying = true;
          this.isPaused = false;
        })
        .catch((e) => {
          console.error('[AudioService] ❌ Failed to resume audio element:', e);
        });
      resumed = true;
    }
    
    if (resumed) {
      // Optimistically set state - will be corrected in .catch() if fails
      this.isPlaying = true;
      this.isPaused = false;
    }
    
    return resumed;
  }
  
  // ============================================================================
  // BUILD 55: FOREGROUND RESUME SUPPORT FOR THERAPY/SOUNDSCAPE
  // ============================================================================
  
  /**
   * BUILD 55: Resume therapy/soundscape playback if needed
   * Called by AudioSessionManager when app returns to foreground
   * @returns {Promise<boolean>} Whether resume was successful
   */
  async resumeTherapyIfNeeded() {
    console.log('[AudioService] resumeTherapyIfNeeded() called');
    console.log(`[AudioService]   isPlaying=${this.isPlaying}, isPaused=${this.isPaused}`);
    console.log(`[AudioService]   audioContext.state=${this.audioContext?.state}`);
    console.log(`[AudioService]   iosSoundSource=${this.iosSoundSource ? 'exists' : 'null'}`);
    console.log(`[AudioService]   iosIsPaused=${this.iosIsPaused}`);
    
    // Skip if user explicitly paused
    if (this.isPaused || this.iosIsPaused) {
      console.log('[AudioService] resumeTherapyIfNeeded: User-paused, skipping auto-resume');
      return false;
    }
    
    // Skip if not playing
    if (!this.isPlaying) {
      console.log('[AudioService] resumeTherapyIfNeeded: Not playing, skipping resume');
      return false;
    }
    
    try {
      // Step 1: Resume AudioContext if suspended
      if (this.audioContext && this.audioContext.state === 'suspended') {
        console.log('[AudioService] resumeTherapyIfNeeded: AudioContext suspended, resuming...');
        await this.audioContext.resume();
        console.log(`[AudioService] resumeTherapyIfNeeded: AudioContext.state=${this.audioContext.state}`);
      }
      
      // Step 2: Check iOS buffer-based playback
      if (this.isIOS) {
        if (!this.iosSoundSource && this.iosSoundBuffer) {
          // Buffer exists but source is gone - recreate
          console.log('[AudioService] resumeTherapyIfNeeded: iOS buffer source lost, restarting...');
          await this.iosPlayBuffer(
            this.iosSoundBuffer,
            this.iosCurrentVolume,
            this.iosIsLooping,
            this.iosOnEndedCallback,
            0 // Start from beginning since we lost position
          );
          console.log('[AudioService] resumeTherapyIfNeeded: ✅ iOS buffer restarted');
          return true;
        } else if (this.iosSoundSource) {
          // Source exists - audio should be playing
          console.log('[AudioService] resumeTherapyIfNeeded: iOS buffer source exists, audio should be playing');
          return true;
        }
      }
      
      // Step 3: Check HTMLMediaElement (non-iOS)
      if (this.streamAudioElement && this.streamAudioElement.paused && !this.streamAudioElement.ended) {
        console.log('[AudioService] resumeTherapyIfNeeded: Stream element paused, calling play()...');
        await this.streamAudioElement.play();
        console.log('[AudioService] resumeTherapyIfNeeded: ✅ Stream element resumed');
        return true;
      }
      
      if (this.audioElement && this.audioElement.paused && !this.audioElement.ended) {
        console.log('[AudioService] resumeTherapyIfNeeded: Audio element paused, calling play()...');
        await this.audioElement.play();
        console.log('[AudioService] resumeTherapyIfNeeded: ✅ Audio element resumed');
        return true;
      }
      
      console.log('[AudioService] resumeTherapyIfNeeded: Already running or no active source');
      return true;
      
    } catch (error) {
      console.error('[AudioService] resumeTherapyIfNeeded: ❌ Resume failed:', error);
      return false;
    }
  }

  /**
   * Check if audio is currently paused (not stopped)
   * @returns {boolean}
   */
  isPausedState() {
    // V1.5: Check iOS-specific pause state first
    if (this.isIOS && this.iosIsPaused) {
      console.log('[AudioService] isPausedState() - iOS paused:', true);
      return true;
    }
    console.log('[AudioService] isPausedState() called, returning:', this.isPaused);
    return this.isPaused === true;
  }

  /**
   * Set master volume
   * @param {number} volume - Volume 0-1
   */
  setVolume(volume, immediate = false) {
    // Use direct volume since audio files are pre-normalized to -20 dBFS
    // This ensures consistent volume with Radio Hearen and Playlists
    const actualVolume = volume;
    
    // V1.4: iOS uses dedicated gain node for buffer playback
    if (this.isIOS) {
      this.iosSetVolume(actualVolume);
    }
    
    // Update master gain for noise sources
    if (this.masterGain && this.audioContext) {
      const currentTime = this.audioContext.currentTime;
      this.masterGain.gain.cancelScheduledValues(currentTime);
      
      if (immediate) {
        // Immediate volume change for test tones (no ramping)
        this.masterGain.gain.setValueAtTime(actualVolume, currentTime);
      } else {
        // Smooth ramping for therapy audio
        this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, currentTime);
        this.masterGain.gain.linearRampToValueAtTime(actualVolume, currentTime + 0.05);
      }
    }
    
    // Update audio element volume for nature sounds (non-iOS only)
    if (!this.isIOS && this.audioElement) {
      this.audioElement.volume = actualVolume;
    }
    
  }

  /**
   * Get spectrum data for visualization
   * @returns {Object} { original: Uint8Array, processed: Uint8Array, sampleRate: number, boostedFrequencies: Array }
   */
  getSpectrumData() {
    if (!this.analyserOriginal || !this.analyserProcessed) {
      return null;
    }
    
    // DEBUG: Force log every call
    
    const bufferLength = this.analyserOriginal.frequencyBinCount;
    const originalData = new Uint8Array(bufferLength);
    const processedData = new Uint8Array(bufferLength);
    
    this.analyserOriginal.getByteFrequencyData(originalData);
    this.analyserProcessed.getByteFrequencyData(processedData);
    
    // Get boosted frequencies from equalizer settings ONLY if toggles are enabled
    const boostedFrequencies = [];
    if (this.equalizerSettings) {
      // Add audiometric compensation frequencies ONLY if enabled
      if (this.audiometricCompensationEnabled && this.equalizerSettings.audiometric_gains) {
        for (const gain of this.equalizerSettings.audiometric_gains) {
          if (gain.gain > 0) {
            boostedFrequencies.push({
              frequency: gain.frequency,
              gain: gain.gain,
              type: 'audiometric'
            });
          }
        }
      }
      
      // Add inverse notch frequency ONLY if enabled
      if (this.inverseNotchEnabled && this.equalizerSettings.inverse_notch) {
        const notchData = {
          frequency: this.equalizerSettings.inverse_notch.center_frequency,
          gain: this.equalizerSettings.inverse_notch.gain,
          bandwidth: this.equalizerSettings.inverse_notch.bandwidth,
          type: 'notch'
        };
        boostedFrequencies.push(notchData);
        console.log('📊 AudioService: Adding notch to spectrum data:', notchData);
      } else {
        console.log('📊 AudioService: Notch NOT added - inverseNotchEnabled:', this.inverseNotchEnabled, 'has inverse_notch:', !!this.equalizerSettings?.inverse_notch);
      }
    }
    
    console.log('📊 AudioService: Returning spectrum data with', boostedFrequencies.length, 'boosted frequencies');
    
    return {
      original: originalData,
      processed: processedData,
      sampleRate: this.audioContext.sampleRate,
      bufferLength: bufferLength,
      boostedFrequencies: boostedFrequencies,
      hasActiveFilters: this.equalizerFilters.length > 0
    };
  }

  /**
   * Check if audio is currently playing
   * @returns {boolean}
   */
  isPlaying() {
    return (this.noiseNode !== null) || 
           (this.streamAudioElement && !this.streamAudioElement.paused) ||
           (this.audioElement && !this.audioElement.paused);
  }

  /**
   * Toggle equalizer on/off
   * @param {boolean} enabled - Enable or disable equalizer
   */
  toggleEqualizer(enabled) {
    this.equalizerEnabled = enabled;
    
    if (this.equalizerFilters && this.equalizerFilters.length > 0) {
      // Toggle all equalizer filters
      this.equalizerFilters.forEach((filter, index) => {
        if (enabled && filter.targetGain !== undefined) {
          filter.gain.value = filter.targetGain;
        } else {
          filter.gain.value = 0;
        }
      });
    } else if (enabled) {
    }
  }

  /**
   * Toggle notch filter on/off
   * @param {boolean} enabled - Enable or disable notch filter
   */
  toggleNotchFilter(enabled) {
    this.notchEnabled = enabled;
    
    if (this.notchFilter) {
      // Toggle the notch filter by adjusting its Q value
      // High Q = narrow notch (active), Low Q = wide pass (inactive)
      this.notchFilter.Q.value = enabled ? 30 : 0.1;
    } else if (enabled) {
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this.stopTone();
    this.stopTherapy();
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.isInitialized = false;
  }
}

// Export singleton instance
const audioService = new AudioService();
export default audioService;
